<template>
  <v-dialog v-model="isOpen" max-width="900" persistent>
    <v-card class="agent-contact-modal elevation-24">
      <!-- Header -->
      <div class="modal-header">
        <div class="header-background">
          <div class="header-gradient"></div>
          <div class="header-shapes">
            <div class="header-shape shape-1"></div>
            <div class="header-shape shape-2"></div>
          </div>
        </div>

        <div class="header-content">
          <div class="agent-info">
            <div class="agent-avatar elevation-8">
              <i class="material-icons-round">support_agent</i>
            </div>
            <div class="agent-details">
              <h3 class="agent-name">Alex</h3>
              <p class="agent-title">Your AutoSyncX™ AI Assistant</p>
            </div>
          </div>
        </div>

        <!-- Close Button (positioned absolutely) -->
        <v-btn
          icon
          @click="closeModal"
          class="close-btn elevation-2"
        >
          <v-icon class="material-icons-round">close</v-icon>
        </v-btn>
      </div>

      <!-- Content -->
      <v-card-text class="modal-content">
        <!-- Chat Mode -->
        <div v-if="currentMode === 'chat'" class="chat-container">
          <div class="chat-messages" ref="chatMessages">
            <div
              v-for="(message, index) in messages"
              :key="index"
              :class="['message', message.sender_type === 'user' ? 'user-message' : 'agent-message']"
            >
              <div class="message-content">
                <div class="message-text">{{ message.message }}</div>
                <div class="message-time">{{ formatTime(message.sent_at) }}</div>
              </div>
            </div>
            <div v-if="isTyping" class="typing-indicator">
              <div class="typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span class="typing-text">Alex is typing...</span>
            </div>
          </div>
          
          <div class="chat-input">
            <!-- Photo Upload Section (shown when in photo_upload state) -->
            <div v-if="signupState === 'photo_upload' && !uploadedPhoto" class="photo-upload-section">
              <input
                type="file"
                ref="photoInput"
                accept="image/*"
                @change="handlePhotoSelect"
                style="display: none"
              />
              <v-btn
                color="blue"
                dark
                @click="$refs.photoInput.click()"
                class="upload-btn"
                block
              >
                <i class="material-icons-round mr-2">add_photo_alternate</i>
                Upload Profile Photo
              </v-btn>
              <p class="upload-hint">Click to browse or drag and drop an image</p>
            </div>

            <!-- Photo Preview (shown after photo selected) -->
            <div v-else-if="signupState === 'photo_upload' && uploadedPhoto" class="photo-preview-section">
              <div class="photo-preview">
                <img :src="photoPreview" alt="Profile Preview" />
                <v-btn
                  icon
                  small
                  color="error"
                  @click="removePhoto"
                  class="remove-photo-btn"
                >
                  <i class="material-icons-round">close</i>
                </v-btn>
              </div>
              <v-btn
                color="green"
                @click="confirmPhotoUpload"
                :disabled="isTyping"
                block
                class="mt-3"
              >
                <i class="material-icons-round mr-2">check</i>
                Confirm Photo
              </v-btn>
            </div>

            <!-- Regular Text Input -->
            <v-text-field
              v-else
              v-model="currentMessage"
              placeholder="Type your message..."
              outlined
              dense
              hide-details
              @keyup.enter="sendMessage"
              :disabled="isTyping"
              class="message-input"
            >
              <template v-slot:append>
                <v-btn
                  icon
                  @click="sendMessage"
                  :disabled="!currentMessage.trim() || isTyping"
                  class="send-btn"
                >
                  <i class="material-icons-round">send</i>
                </v-btn>
              </template>
            </v-text-field>
          </div>
        </div>

        <!-- Voice Mode -->
        <div v-else-if="currentMode === 'voice'" class="voice-container">
          <div class="voice-status">
            <div class="connection-status" :class="connectionStatus.type">
              <i class="material-icons-round">{{ connectionStatus.icon }}</i>
              <span>{{ connectionStatus.message }}</span>
            </div>
          </div>

          <!-- Voice Controls -->
          <div class="text-center mb-6">
            <div class="microphone-container">
              <v-btn
                :color="isVoiceCallActive ? 'success' : 'white'"
                icon
                size="x-large"
                :class="{ 'realtime-active': isVoiceCallActive }"
                @click="toggleVoiceConversation"
                :disabled="connectionStatus.type === 'error'"
                class="microphone-button"
              >
                <i class="material-icons-round" :style="{ color: isVoiceCallActive ? 'white' : '#333' }">{{ isVoiceCallActive ? 'mic' : 'mic_off' }}</i>
              </v-btn>

              <!-- Mute Button (only show when call is active) -->
              <v-btn
                v-if="isVoiceCallActive"
                :color="isMuted ? 'red' : 'grey'"
                icon
                dark
                size="large"
                @click="toggleMute"
                class="mute-button ml-4"
              >
                <i class="material-icons-round">{{ isMuted ? 'mic_off' : 'mic' }}</i>
              </v-btn>
            </div>

            <p class="text-h6 mt-4">
              {{ isVoiceCallActive ? 'Real-time conversation active' : 'Click to start voice conversation' }}
            </p>

            <p class="text-body-2 text-grey-darken-1">
              {{ connectionStatus.type === 'success' ? 'Connected to Alex from AutoSyncX™' : 'Connecting...' }}
            </p>
          </div>

          <!-- Voice Transcript -->
          <div v-if="voiceTranscript.length > 0" class="voice-transcript">
            <h4 class="transcript-title">Conversation</h4>
            <div class="transcript-messages">
              <div
                v-for="(message, index) in voiceTranscript"
                :key="index"
                :class="['transcript-message', message.role]"
              >
                <strong>{{ message.role === 'user' ? 'You' : 'Alex' }}:</strong>
                {{ message.content }}
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { Conversation } from '@elevenlabs/client'

export default {
  name: 'AgentContactModal',
  props: {
    mode: {
      type: String,
      default: 'chat'
    },
    userId: {
      type: String,
      default: null
    },
    program: {
      type: String,
      default: ''
    },
    agentType: {
      type: String,
      default: null // 'referring' or 'sales'
    }
  },
  data() {
    return {
      isOpen: true,
      currentMode: this.mode,
      messages: [],
      currentMessage: '',
      isTyping: false,
      sessionId: null,
      conversationId: null,
      isVoiceCallActive: false,
      voiceTranscript: [],
      connectionStatus: {
        type: 'success',
        icon: 'check_circle',
        message: 'Connected'
      },
      // Voice agent data
      agentId: null,
      signedUrl: null,
      conversation: null,
      isSpeaking: false,
      isListening: false,
      isMuted: false,
      microphoneStream: null, // Store microphone stream for mute control
      // Agent chat signup data
      isInSignupFlow: false,
      signupSessionId: null,
      signupState: 'greeting',
      collectedData: {},
      registrationCompleted: false,
      uploadedPhoto: null,
      photoPreview: null,
      showPhotoUpload: false
    }
  },
  computed: {
    voiceIcon() {
      if (this.isSpeaking) return 'record_voice_over'
      if (this.isListening) return 'hearing'
      if (this.isVoiceCallActive) return 'phone_in_talk'
      return 'phone'
    },
    voiceStatusText() {
      if (this.isSpeaking) return 'Alex is speaking...'
      if (this.isListening) return 'Listening...'
      if (this.isVoiceCallActive) return 'Call Active'
      return 'Ready to Call'
    },
    voiceDescription() {
      if (this.isVoiceCallActive) return 'You are now connected with Alex. Speak naturally!'
      return 'Click to start a voice conversation with Alex, your AutoSyncX™ representative.'
    }
  },
  watch: {
    currentMode(newMode, oldMode) {
      console.log('🔄 Mode changed from', oldMode, 'to', newMode)

      // Stop voice conversation when switching away from voice mode
      if (oldMode === 'voice' && this.isVoiceCallActive) {
        console.log('🔇 Stopping voice conversation due to mode switch')
        this.stopConversation()
      }

      // Initialize the new mode
      if (newMode === 'chat') {
        this.initializeChat()
      } else if (newMode === 'voice') {
        this.initializeVoice()
      }
    }
  },
  mounted() {
    if (this.currentMode === 'chat') {
      this.initializeChat()
    } else if (this.currentMode === 'voice') {
      this.initializeVoice()
    }
  },

  beforeUnmount() {
    if (this.isVoiceCallActive) {
      this.stopConversation()
    }
  },
  methods: {
    closeModal() {
      this.isOpen = false
      this.$emit('close')
    },
    
    // Chat Methods
    async initializeChat() {
      try {
        console.log('🎤 Initializing chat with agent voice agent...')
        const response = await axios.post('/api/agent/voice-agent/create-conversation')

        if (response.data.flag === 1) {
          this.conversationId = response.data.data.conversation_id
          console.log('✅ Chat initialized:', this.conversationId)
        } else {
          // Fallback to session ID if API fails
          this.sessionId = this.generateSessionId()
        }

        // Initialize agent chat signup to get the initial greeting
        await this.initializeAgentChatSignup()

      } catch (error) {
        console.error('❌ Error initializing chat:', error)
        // Fallback to session ID
        this.sessionId = this.generateSessionId()
        this.$toast.error('Failed to initialize chat. Please try again.')
      }
    },

    async sendMessage() {
      if (!this.currentMessage.trim() || this.isTyping) return

      const userMessage = this.currentMessage.trim()
      this.currentMessage = ''

      // Add user message to chat
      this.addMessage({
        sender_type: 'user',
        message: userMessage,
        sent_at: new Date().toISOString()
      })

      // Simulate agent response
      this.simulateAgentResponse(userMessage)
    },

    addMessage(message) {
      this.messages.push(message)
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },

    async simulateAgentResponse(userMessage) {
      this.isTyping = true

      try {
        // Check for signup intent
        const signupKeywords = ['yes', 'let\'s do it', 'get started', 'sign up', 'signup', 'join', 'register', 'digital card', 'referral card']
        const isSignupIntent = signupKeywords.some(keyword =>
          userMessage.toLowerCase().includes(keyword.toLowerCase())
        )

        if (this.isInSignupFlow || isSignupIntent) {
          // Handle agent chat signup with OpenAI
          await this.handleAgentChatSignup(userMessage)
          return
        }

        // For non-signup messages, initialize agent chat signup
        await this.handleAgentChatSignup(userMessage)
      } catch (error) {
        console.error('❌ Error in agent response:', error)
        this.isTyping = false
      }
    },

    async handleAgentChatSignup(userMessage) {
      try {
        if (!this.isInSignupFlow) {
          // Initialize agent chat signup
          await this.initializeAgentChatSignup()
        }

        // Process the message through OpenAI
        await this.processAgentChatMessage(userMessage)

      } catch (error) {
        console.error('❌ Error in agent chat signup:', error)
        this.addMessage({
          sender_type: 'agent',
          message: 'Sorry, there was an error processing your request. Please try again.',
          sent_at: new Date().toISOString()
        })
      } finally {
        this.isTyping = false
      }
    },

    async initializeAgentChatSignup() {
      try {
        // Pass agent_type if preselected from URL
        const payload = {}
        if (this.agentType) {
          payload.agent_type = this.agentType // 'referring' or 'sales'
        }

        const response = await axios.post('/api/agent-chat-signup/init', payload)

        if (response.data.flag === 1) {
          this.signupSessionId = response.data.session_id
          this.isInSignupFlow = true

          // Add initial greeting from OpenAI
          this.addMessage({
            sender_type: 'agent',
            message: response.data.greeting,
            sent_at: new Date().toISOString()
          })

          console.log('✅ Agent chat signup initialized:', this.signupSessionId)
          console.log('📋 Agent type:', this.agentType || 'not preselected')
        } else {
          throw new Error('Failed to initialize agent chat signup')
        }
      } catch (error) {
        console.error('❌ Error initializing agent chat signup:', error)
        throw error
      }
    },

    async processAgentChatMessage(userMessage) {
      try {
        console.log('📤 Sending message to API:', userMessage)

        const response = await axios.post('/api/agent-chat-signup/message', {
          session_id: this.signupSessionId,
          message: userMessage
        })

        console.log('📥 API Response:', response.data)

        if (response.data.flag === 1) {
          this.signupState = response.data.conversation_state
          this.collectedData = response.data.collected_data

          console.log('📊 Updated state:', this.signupState)
          console.log('📊 Collected data:', this.collectedData)

          // Add AI response
          this.addMessage({
            sender_type: 'agent',
            message: response.data.message,
            sent_at: new Date().toISOString()
          })

          // Check if registration is complete (and not already completed)
          if (response.data.is_complete && !this.registrationCompleted) {
            console.log('✅ Registration complete, calling completeAgentChatSignup...')
            await this.completeAgentChatSignup()
          } else if (response.data.is_complete && this.registrationCompleted) {
            console.log('ℹ️ Registration already completed, skipping duplicate registration')
          }
        } else {
          throw new Error(response.data.message || 'Failed to process message')
        }
      } catch (error) {
        console.error('❌ Error processing agent chat message:', error)
        console.error('❌ Error details:', error.response?.data || error.message)
        throw error
      }
    },

    async completeAgentChatSignup() {
      try {
        console.log('🎯 Starting registration completion...')
        console.log('📸 Uploaded photo:', this.uploadedPhoto ? this.uploadedPhoto.name : 'No photo')
        console.log('👤 Referring user ID (ref_id):', this.userId)
        console.log('🎯 Program:', this.program)

        // Prepare form data with photo
        const formData = new FormData()
        formData.append('session_id', this.signupSessionId)

        // Add ref_id (referring agent's user_id) if provided
        if (this.userId) {
          formData.append('ref_id', this.userId)
          console.log('✅ Added ref_id to form data:', this.userId)
        }

        // Add program if provided
        if (this.program) {
          formData.append('program', this.program)
          console.log('✅ Added program to form data:', this.program)
        }

        if (this.uploadedPhoto) {
          formData.append('file_photo', this.uploadedPhoto)
          console.log('✅ Photo added to form data')
        } else {
          console.warn('⚠️ No photo uploaded!')
        }

        console.log('📤 Sending registration request...')
        const response = await axios.post('/api/agent-chat-signup/complete', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        console.log('📥 Registration response:', response)
        console.log('📥 Registration response data:', response.data)
        console.log('📥 Response data type:', typeof response.data)

        // Handle response data
        let responseData = response.data

        // If response.data is a string, try to parse it
        if (typeof responseData === 'string') {
          try {
            responseData = JSON.parse(responseData)
            console.log('✅ Parsed string response to object:', responseData)
          } catch (e) {
            console.error('❌ Failed to parse response string:', e)
            throw new Error('Invalid response format from server')
          }
        }

        console.log('📥 Final response data:', responseData)
        console.log('📥 Response flag:', responseData.flag)

        if (responseData.flag === 1) {
          this.registrationCompleted = true
          console.log('✅ Agent registration completed:', responseData)

          // Format referral link as subdomain
          const domain = responseData.domain
          const referralLink = `https://${domain}.autosyncx.com`

          this.addMessage({
            sender_type: 'agent',
            message: `All set! 🎯 I've created your digital card.\n\nHere's your referral link:\n👉 ${referralLink}\n\nNow, just share me (Alex) with others. I'll explain everything and sign them up under you. The bigger your team, the more you'll earn.`,
            sent_at: new Date().toISOString()
          })

          this.$toast.success('Welcome to AutoSyncX™! Your referral card has been created.')

          // Emit signup complete event with user data
          this.$emit('signup-complete', {
            user: responseData.data,
            referral_link: referralLink,
            domain: domain
          })
        } else if (responseData.flag === 3) {
          // Email already exists
          this.addMessage({
            sender_type: 'agent',
            message: 'It looks like you already have an account with that email. Would you like to try a different email address?',
            sent_at: new Date().toISOString()
          })
        } else if (responseData.flag === 0) {
          // Validation or other error
          const errorMessage = responseData.message || 'Registration failed'
          console.error('❌ Registration error:', errorMessage, responseData.errors)
          this.addMessage({
            sender_type: 'agent',
            message: `Sorry, there was an issue: ${errorMessage}. Please try again.`,
            sent_at: new Date().toISOString()
          })
        } else {
          throw new Error(responseData.message || 'Failed to complete registration')
        }
      } catch (error) {
        console.error('❌ Error completing agent chat signup:', error)
        console.error('❌ Error response:', error.response)

        let errorMessage = 'Sorry, there was an error completing your registration. Please try again or contact support.'

        // Try to extract error message from response
        if (error.response && error.response.data) {
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message
          }
        }

        this.addMessage({
          sender_type: 'agent',
          message: errorMessage,
          sent_at: new Date().toISOString()
        })
      }
    },

    // Photo Upload Methods
    handlePhotoSelect(event) {
      const file = event.target.files[0]
      if (file) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          this.$toast.error('Please select a valid image file')
          return
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          this.$toast.error('Image size must be less than 5MB')
          return
        }

        this.uploadedPhoto = file

        // Create preview
        const reader = new FileReader()
        reader.onload = (e) => {
          this.photoPreview = e.target.result
        }
        reader.readAsDataURL(file)

        console.log('✅ Photo selected:', file.name)
      }
    },

    removePhoto() {
      this.uploadedPhoto = null
      this.photoPreview = null
      if (this.$refs.photoInput) {
        this.$refs.photoInput.value = ''
      }
    },

    async confirmPhotoUpload() {
      if (!this.uploadedPhoto) {
        this.$toast.error('Please select a photo first')
        return
      }

      try {
        this.isTyping = true

        // Add user confirmation message
        this.addMessage({
          sender_type: 'user',
          message: 'Photo uploaded ✓',
          sent_at: new Date().toISOString()
        })

        // Process through OpenAI to move to complete state
        await this.processAgentChatMessage('Photo uploaded and confirmed')
      } catch (error) {
        console.error('❌ Error confirming photo upload:', error)
        this.$toast.error('Failed to process photo upload')
      } finally {
        this.isTyping = false
      }
    },

    // Voice Methods
    async initializeVoice() {
      this.connectionStatus = {
        type: 'info',
        message: 'Connecting to voice agent...'
      }

      try {
        const response = await axios.post('/api/agent/voice-agent/create-conversation')

        if (response.data.flag === 1 && response.data.data) {
          this.agentId = response.data.data.agent_id
          this.signedUrl = response.data.data.signed_url

          console.log('📋 Agent ID:', this.agentId)
          console.log('📋 Signed URL:', this.signedUrl)

          this.connectionStatus = {
            type: 'success',
            message: 'Ready to start voice conversation!'
          }

          // Don't initialize microphone upfront - do it when user clicks button
          console.log('✅ Voice agent ready - microphone will be initialized when user clicks button')

        } else {
          throw new Error('Voice agent not available')
        }
      } catch (error) {
        console.error('❌ Error initializing voice:', error)
        this.connectionStatus = {
          type: 'error',
          message: 'Failed to connect to voice agent'
        }
        this.$toast.error('Failed to initialize voice. Please try chat mode.')
      }
    },

    toggleVoiceConversation() {
      if (this.isVoiceCallActive) {
        this.stopConversation()
      } else {
        this.startConversation()
      }
    },

    async startConversation() {
      if (!this.signedUrl) {
        this.$toast.error('Voice agent not ready. Please try again.')
        return
      }

      try {
        console.log('🎤 Starting ElevenLabs conversation...')

        // Get microphone access first
        try {
          this.microphoneStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true
            }
          })
          console.log('✅ Microphone access granted')
          console.log('📋 Microphone stream tracks:', this.microphoneStream.getAudioTracks())
        } catch (micError) {
          console.error('❌ Microphone access denied:', micError)
          this.$toast.error('Microphone access is required for voice conversation')
          return
        }

        // Start ElevenLabs conversation using the official SDK
        this.conversation = await Conversation.startSession({
          signedUrl: this.signedUrl,
          clientTools: {
            // Pass custom audio stream if supported
            audioStream: this.microphoneStream
          },
          onConnect: () => {
            console.log('✅ ElevenLabs conversation connected')
            this.isVoiceCallActive = true
            this.connectionStatus = {
              type: 'success',
              message: 'Real-time conversation active - speak naturally!'
            }
            this.$toast.success('Voice conversation started! You can now talk with Alex.')
          },
          onDisconnect: () => {
            console.log('🔌 ElevenLabs conversation disconnected')
            this.isVoiceCallActive = false
            this.connectionStatus = {
              type: 'info',
              message: 'Conversation ended'
            }

            // Stop microphone stream
            if (this.microphoneStream) {
              this.microphoneStream.getTracks().forEach(track => track.stop())
              this.microphoneStream = null
            }
          },
          onError: (error) => {
            console.error('❌ ElevenLabs conversation error:', error)
            this.isVoiceCallActive = false
            this.connectionStatus = {
              type: 'error',
              message: 'Connection error'
            }
            this.$toast.error('Voice conversation error. Please try again.')

            // Stop microphone stream
            if (this.microphoneStream) {
              this.microphoneStream.getTracks().forEach(track => track.stop())
              this.microphoneStream = null
            }
          },
          onModeChange: (mode) => {
            console.log('🔄 ElevenLabs mode change:', mode)
            this.isSpeaking = mode.mode === 'speaking'
            this.isListening = mode.mode === 'listening'
          }
        })

      } catch (error) {
        console.error('❌ Error starting conversation:', error)
        this.$toast.error('Failed to start voice conversation. Please try again.')
        this.connectionStatus = {
          type: 'error',
          message: 'Failed to start conversation'
        }
      }
    },

    async stopConversation() {
      try {
        if (this.conversation) {
          await this.conversation.endSession()
          this.conversation = null
        }

        // Stop microphone stream
        if (this.microphoneStream) {
          this.microphoneStream.getTracks().forEach(track => track.stop())
          this.microphoneStream = null
          console.log('🎤 Microphone stream stopped')
        }

        this.isVoiceCallActive = false
        this.isSpeaking = false
        this.isListening = false
        this.isMuted = false
        this.connectionStatus = {
          type: 'info',
          message: 'Conversation ended'
        }
        console.log('🔌 Voice conversation stopped')
      } catch (error) {
        console.error('❌ Error stopping conversation:', error)
        this.isMuted = false

        // Ensure microphone is stopped even on error
        if (this.microphoneStream) {
          this.microphoneStream.getTracks().forEach(track => track.stop())
          this.microphoneStream = null
        }
      }
    },

    async toggleMute() {
      if (!this.conversation || !this.isVoiceCallActive) {
        console.warn('⚠️ No active conversation to mute')
        return
      }

      try {
        console.log('🔄 Toggle mute called. Current state:', this.isMuted)

        if (this.isMuted) {
          // Unmute - use ElevenLabs SDK method
          console.log('🎤 Unmuting microphone using setMicMuted(false)...')
          this.conversation.setMicMuted(false)
          this.isMuted = false
          this.$toast.info('Microphone unmuted')
          console.log('✅ Microphone unmuted')
        } else {
          // Mute - use ElevenLabs SDK method
          console.log('🔇 Muting microphone using setMicMuted(true)...')
          this.conversation.setMicMuted(true)
          this.isMuted = true
          this.$toast.info('Microphone muted')
          console.log('✅ Microphone muted')
        }
      } catch (error) {
        console.error('❌ Error toggling mute:', error)
        this.$toast.error('Failed to toggle microphone')
      }
    },

    // Utility Methods
    generateSessionId() {
      return 'agent_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now()
    },

    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },

    scrollToBottom() {
      const chatMessages = this.$refs.chatMessages
      if (chatMessages) {
        chatMessages.scrollTop = chatMessages.scrollHeight
      }
    }
  }
}
</script>

<style scoped>
.agent-contact-modal {
  border-radius: 24px !important;
  overflow: hidden;
}

/* Header Styles */
.modal-header {
  position: relative;
  padding: 32px 32px;
  overflow: hidden;
}

.header-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.header-gradient {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.header-shapes {
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.header-shape {
  position: absolute;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
}

.header-shape.shape-1 {
  width: 150px;
  height: 150px;
  top: -50px;
  right: -30px;
  animation: float 6s ease-in-out infinite;
}

.header-shape.shape-2 {
  width: 100px;
  height: 100px;
  bottom: -30px;
  left: -20px;
  animation: float 8s ease-in-out infinite reverse;
}

@keyframes float {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  50% {
    transform: translate(10px, -10px) scale(1.05);
  }
}

.header-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  width: 100%;
}

.agent-info {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.agent-avatar {
  background: white;
  border-radius: 50%;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #667eea;
  animation: avatarPulse 3s ease-in-out infinite;
}

@keyframes avatarPulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.agent-details h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 800;
  color: white;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.agent-details p {
  margin: 4px 0 0 0;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.95);
}

.close-btn {
  position: absolute !important;
  top: 16px !important;
  right: 16px !important;
  z-index: 2 !important;
  background: rgba(255, 255, 255, 0.2) !important;
  color: white !important;
  transition: all 0.3s ease !important;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3) !important;
  transform: rotate(90deg);
}

.modal-content {
  padding: 0;
  height: 550px;
}

/* Chat Styles */
.chat-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%);
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  background: transparent;
}

.chat-messages::-webkit-scrollbar {
  width: 6px;
}

.chat-messages::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: #667eea;
  border-radius: 10px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: #764ba2;
}

.message {
  margin-bottom: 20px;
  display: flex;
  animation: messageSlideIn 0.3s ease-out;
}

@keyframes messageSlideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.user-message {
  justify-content: flex-end;
}

.agent-message {
  justify-content: flex-start;
}

.message-content {
  max-width: 75%;
  padding: 14px 18px;
  border-radius: 20px;
  position: relative;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;
}

.message-content:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.user-message .message-content {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-bottom-right-radius: 6px;
}

.agent-message .message-content {
  background: white;
  color: #2c3e50;
  border: 1px solid #e9ecef;
  border-bottom-left-radius: 6px;
}

.message-text {
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 6px;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.message-time {
  font-size: 0.7rem;
  opacity: 0.7;
  text-align: right;
}

.typing-indicator {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  background: white;
  border-radius: 20px;
  border-bottom-left-radius: 6px;
  max-width: 140px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  animation: messageSlideIn 0.3s ease-out;
}

.typing-dots {
  display: flex;
  gap: 5px;
}

.typing-dots span {
  width: 8px;
  height: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  animation: typing 1.4s infinite ease-in-out;
}

.typing-dots span:nth-child(1) { animation-delay: -0.32s; }
.typing-dots span:nth-child(2) { animation-delay: -0.16s; }

@keyframes typing {
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.typing-text {
  font-size: 0.8rem;
  color: #6c757d;
  font-weight: 500;
}

.chat-input {
  padding: 20px 24px;
  background: white;
  border-top: 2px solid #f0f0f0;
}

.message-input {
  border-radius: 28px !important;
}

.message-input >>> .v-input__control {
  border-radius: 28px !important;
}

.message-input >>> fieldset {
  border-color: #e9ecef !important;
  border-width: 2px !important;
  transition: all 0.3s ease !important;
}

.message-input >>> .v-input--is-focused fieldset {
  border-color: #667eea !important;
}

.send-btn {
  color: #667eea !important;
  transition: all 0.3s ease !important;
}

.send-btn:hover {
  transform: scale(1.1);
  color: #764ba2 !important;
}

/* Photo Upload Styles */
.photo-upload-section {
  padding: 32px;
  text-align: center;
  background: linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%);
}

.upload-btn {
  margin-bottom: 12px;
  border-radius: 16px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  padding: 24px !important;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
}

.upload-hint {
  font-size: 0.9rem;
  color: #6c757d;
  margin: 12px 0 0 0;
  font-weight: 500;
}

.photo-preview-section {
  padding: 32px;
  text-align: center;
  background: linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%);
}

.photo-preview {
  position: relative;
  display: inline-block;
  margin-bottom: 24px;
}

.photo-preview img {
  width: 180px;
  height: 180px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid #667eea;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;
}

.photo-preview img:hover {
  transform: scale(1.05);
  box-shadow: 0 12px 32px rgba(102, 126, 234, 0.4);
}

.remove-photo-btn {
  position: absolute;
  top: -8px;
  right: -8px;
  background: white !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
  transition: all 0.3s ease !important;
}

.remove-photo-btn:hover {
  transform: scale(1.1);
}

/* Voice Styles */
.voice-container {
  height: 100%;
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%);
}

.voice-status {
  margin-bottom: 40px;
}

.connection-status {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  border-radius: 24px;
  font-size: 0.95rem;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  animation: fadeInUp 0.5s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.connection-status.success {
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  color: #155724;
}

.connection-status.info {
  background: linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%);
  color: #0c5460;
}

.connection-status.error {
  background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
  color: #721c24;
}

.microphone-container {
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
}

.microphone-button {
  width: 100px !important;
  height: 100px !important;
  border: 5px solid #e9ecef !important;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1) !important;
}

.microphone-button:hover {
  transform: scale(1.05);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15) !important;
}

.microphone-button.realtime-active {
  border-color: #28a745 !important;
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%) !important;
  animation: voicePulse 2s infinite;
}

@keyframes voicePulse {
  0% {
    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.7);
  }
  50% {
    box-shadow: 0 0 0 20px rgba(40, 167, 69, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0);
  }
}

.mute-button {
  transition: all 0.3s ease !important;
}

.mute-button:hover {
  transform: scale(1.1);
}

.voice-transcript {
  width: 100%;
  max-width: 650px;
  margin-top: 40px;
}

.transcript-title {
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 20px;
  color: #2c3e50;
  text-align: left;
}

.transcript-messages {
  background: white;
  border-radius: 16px;
  padding: 20px;
  max-height: 220px;
  overflow-y: auto;
  border: 2px solid #e9ecef;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.transcript-messages::-webkit-scrollbar {
  width: 6px;
}

.transcript-messages::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.transcript-messages::-webkit-scrollbar-thumb {
  background: #667eea;
  border-radius: 10px;
}

.transcript-message {
  margin-bottom: 16px;
  font-size: 0.95rem;
  line-height: 1.5;
  padding: 8px 0;
}

.transcript-message:last-child {
  margin-bottom: 0;
}

.transcript-message.user {
  color: #667eea;
  font-weight: 600;
}

.transcript-message.assistant {
  color: #495057;
}

.transcript-message strong {
  font-weight: 700;
  margin-right: 8px;
}

/* Responsive Design */
@media (max-width: 960px) {
  .modal-header {
    padding: 24px 20px;
  }

  .agent-avatar {
    width: 56px;
    height: 56px;
    font-size: 1.75rem;
  }

  .agent-details h3 {
    font-size: 1.3rem;
  }

  .modal-content {
    height: 500px;
  }
}

@media (max-width: 600px) {
  .modal-content {
    height: 450px;
  }

  .modal-header {
    padding: 20px 16px;
  }

  .header-content {
    flex-direction: column;
    gap: 16px;
  }

  .agent-info {
    width: 100%;
  }

  .header-actions {
    width: 100%;
    justify-content: space-between;
  }

  .agent-avatar {
    width: 48px;
    height: 48px;
    font-size: 1.5rem;
  }

  .agent-details h3 {
    font-size: 1.1rem;
  }

  .agent-details p {
    font-size: 0.85rem;
  }

  .chat-messages {
    padding: 16px;
  }

  .message-content {
    max-width: 85%;
    padding: 12px 14px;
  }

  .chat-input {
    padding: 16px;
  }

  .voice-container {
    padding: 24px 16px;
  }

  .microphone-button {
    width: 80px !important;
    height: 80px !important;
  }

  .photo-upload-section,
  .photo-preview-section {
    padding: 24px 16px;
  }

  .photo-preview img {
    width: 140px;
    height: 140px;
  }
}
</style>
